<template>
  <article class="mobile-home">
    <section id="profile" class="mobile-hero">
      <h1>{{ profile.headline }}</h1>

      <div class="mobile-hero__tag">
        <MobileSymbolIcon type="compass" />
        <span>{{ profile.roleLine }}</span>
      </div>

      <p>{{ profile.intro }}</p>
    </section>

    <section class="mobile-section mobile-work-profile">
      <header class="mobile-section__header">
        <span>Arbeitsprofil</span>
        <h2>Wie ich Verantwortung übernehme.</h2>
        <p>
          Entwicklungserfahrung bleibt die Grundlage. Der Schwerpunkt liegt
          heute in Analyse, Aufbau, Softwarearchitektur, KI/RAG und Führung.
        </p>
      </header>

      <div class="mobile-flow">
        <article v-for="(item, index) in heroFocusItems" :key="item.title">
          <small>{{ String(index + 1).padStart(2, "0") }}</small>
          <div>
            <strong>{{ item.title }}</strong>
            <p>{{ item.detail }}</p>
          </div>
        </article>
      </div>
    </section>

    <section id="expertise" class="mobile-section mobile-expertise">
      <header class="mobile-section__header">
        <span>Expertise</span>
        <h2>Womit ich diese Rolle fülle.</h2>
        <p>
          Die einzelnen Fähigkeiten sind wichtig. Entscheidend ist ihr
          Zusammenspiel aus Produkt, Softwarearchitektur, KI/RAG und operativer
          Klarheit.
        </p>
      </header>

      <div class="mobile-accordion-list">
        <article
          v-for="(pillar, index) in expertisePillars"
          :key="pillar.title"
          class="mobile-accordion"
          :class="{ 'mobile-accordion--open': isPillarOpen(pillar.title) }"
        >
          <button
            class="mobile-accordion__summary"
            type="button"
            :aria-expanded="isPillarOpen(pillar.title)"
            @click="togglePillar(pillar.title)"
          >
            <MobileSymbolIcon :type="getPillarIcon(index)" />
            <div>
              <strong>{{ pillar.title }}</strong>
              <span>{{ pillar.summary }}</span>
            </div>
          </button>
          <Transition name="mobile-disclosure">
            <ul v-if="isPillarOpen(pillar.title)">
              <li v-for="point in pillar.points" :key="point">{{ point }}</li>
            </ul>
          </Transition>
        </article>
      </div>
    </section>

    <section class="mobile-section mobile-ai">
      <header class="mobile-section__header">
        <span>KI & RAG</span>
        <h2>Wissenssysteme, die im Alltag tragen.</h2>
        <p>
          RAG ist Architekturarbeit: Daten vorbereiten, Suche bewerten, Kontext
          steuern, Antworten belegen und Grenzen sichtbar halten.
        </p>
      </header>

      <div class="mobile-ai__cards">
        <article v-for="item in aiExpertiseItems" :key="item.title">
          <MobileSymbolIcon type="brain" />
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
          <div>
            <span v-for="method in item.methods" :key="method">
              {{ method }}
            </span>
          </div>
        </article>
      </div>
    </section>

    <section class="mobile-section mobile-modes">
      <header class="mobile-section__header">
        <span>Anknüpfung</span>
        <h2>Wo ein Gespräch sinnvoll ist.</h2>
      </header>

      <div class="mobile-card-list">
        <article v-for="mode in workModes" :key="mode.title">
          <small>{{ mode.status }}</small>
          <strong>{{ mode.title }}</strong>
          <p>{{ mode.description }}</p>
        </article>
      </div>
    </section>

    <section class="mobile-section mobile-evidence">
      <header class="mobile-section__header">
        <span>Praxis</span>
        <h2>Wo diese Arbeitsweise praktisch wird.</h2>
      </header>

      <div class="mobile-evidence__timeline">
        <article v-for="item in evidenceItems" :key="item.title">
          <small>{{ item.label }}</small>
          <h3>{{ item.title }}</h3>
          <p>{{ item.context }}</p>
          <strong>{{ item.outcome }}</strong>
        </article>
      </div>
    </section>

    <section id="contact" class="mobile-section mobile-contact">
      <header class="mobile-section__header">
        <span>Nächster Schritt</span>
        <h2>
          Wenn es um Softwarearchitektur, technische Leitung oder Vecura geht,
          lass uns sprechen.
        </h2>
        <p>
          E-Mail ist der direkte Weg für konkrete Anfragen. LinkedIn nutze ich
          für beruflichen Kontext und Austausch. Alles rund um Vecura läuft über
          die Vecura-Website oder die Unternehmensmail.
        </p>
      </header>

      <div class="mobile-contact__person">
        <figure>
          <img
            :src="contactPortrait"
            alt="Portrait von Timo Rothdeutsch"
            loading="lazy"
            decoding="async"
          />
        </figure>
        <div>
          <strong>Timo Rothdeutsch</strong>
          <span>Mitgründer & COO bei Vecura</span>
          <small>
            Softwarearchitektur, KI/RAG und technische Verantwortung.
          </small>
        </div>
      </div>

      <div class="mobile-contact__actions" aria-label="Kontaktwege">
        <a
          v-for="channel in contactChannels"
          :key="channel.href"
          class="mobile-contact-action"
          :href="channel.href"
          :target="isExternalLink(channel.href) ? '_blank' : undefined"
          :rel="
            isExternalLink(channel.href) ? 'noopener noreferrer' : undefined
          "
          :aria-label="`${channel.label}: ${channel.value}`"
        >
          <ContactChannelIcon
            class="mobile-contact-action__icon"
            :type="getChannelIcon(channel.label)"
          />
        </a>
      </div>
    </section>
  </article>
</template>

<script setup lang="ts">
import { ref } from "vue";
import ContactChannelIcon from "@/components/icons/ContactChannelIcon.vue";
import MobileSymbolIcon from "@/components/icons/MobileSymbolIcon.vue";
import contactPortrait from "@/assets/timo-contact-portrait.jpg";
import {
  aiExpertiseItems,
  contactChannels,
  evidenceItems,
  expertisePillars,
  heroFocusItems,
  profile,
  workModes,
} from "@/data/profile";

const openPillarTitles = ref<Set<string>>(
  new Set(expertisePillars[0] ? [expertisePillars[0].title] : [])
);

function getPillarIcon(
  index: number
): "briefcase" | "compass" | "brain" | "shield" {
  const icons = ["briefcase", "compass", "brain", "shield"] as const;
  return icons[index] ?? "briefcase";
}

function getChannelIcon(label: string): "mail" | "linkedin" | "vecura" {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel.includes("mail")) {
    return "mail";
  }

  if (normalizedLabel.includes("linkedin")) {
    return "linkedin";
  }

  return "vecura";
}

function isExternalLink(href: string): boolean {
  return href.startsWith("http");
}

function isPillarOpen(title: string): boolean {
  return openPillarTitles.value.has(title);
}

function togglePillar(title: string): void {
  const nextOpenTitles = new Set(openPillarTitles.value);

  if (nextOpenTitles.has(title)) {
    nextOpenTitles.delete(title);
  } else {
    nextOpenTitles.add(title);
  }

  openPillarTitles.value = nextOpenTitles;
}
</script>

<style scoped>
.mobile-home {
  display: none;
  color: var(--color-ink);
}

.mobile-hero,
.mobile-section {
  padding: 28px 18px;
}

.mobile-hero {
  min-height: calc(100svh - var(--site-header-height) - 82px);
  background: var(--color-paper);
  padding-top: 38px;
}

.mobile-hero__tag {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  margin-top: 22px;
  color: var(--color-muted);
  font-size: 0.72rem;
  font-weight: 900;
  line-height: 1.35;
  text-transform: uppercase;
}

.mobile-hero__tag svg {
  width: 17px;
  height: 17px;
  flex: 0 0 auto;
  color: var(--color-yellow);
}

h1 {
  margin: 0;
  color: var(--color-ink);
  font-size: clamp(2.12rem, 10.8vw, 2.86rem);
  line-height: 1.03;
  text-wrap: balance;
  hyphens: auto;
  overflow-wrap: anywhere;
}

.mobile-hero > p {
  margin: 22px 0 0;
  color: var(--color-muted);
  font-size: 1rem;
  line-height: 1.62;
}

.mobile-section {
  border-top: 1px solid var(--color-line);
  background: var(--color-paper-strong);
}

.mobile-section:nth-of-type(odd) {
  background: var(--color-paper);
}

.mobile-section__header {
  display: grid;
  gap: 10px;
  margin-bottom: 20px;
}

.mobile-section__header > span {
  color: var(--color-muted);
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.mobile-section__header h2 {
  margin: 0;
  color: var(--color-ink);
  font-size: 1.72rem;
  line-height: 1.12;
  text-wrap: balance;
}

.mobile-section__header p {
  margin: 0;
  color: var(--color-muted);
  font-size: 0.98rem;
  line-height: 1.58;
}

.mobile-flow {
  display: grid;
  gap: 10px;
}

.mobile-flow article {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 12px;
  border: 1px solid var(--color-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.24);
  padding: 15px;
}

.mobile-flow small {
  color: var(--color-yellow);
  font-size: 0.74rem;
  font-weight: 950;
}

.mobile-flow strong,
.mobile-card-list strong {
  display: block;
  color: var(--color-ink-soft);
  overflow-wrap: anywhere;
}

.mobile-flow p,
.mobile-card-list p,
.mobile-evidence p,
.mobile-evidence strong {
  margin: 6px 0 0;
  color: var(--color-muted);
  font-size: 0.92rem;
  line-height: 1.52;
}

.mobile-accordion-list,
.mobile-card-list {
  display: grid;
  gap: 10px;
}

.mobile-accordion {
  border: 1px solid var(--color-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.24);
  overflow: hidden;
}

.mobile-accordion__summary {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 28px;
  gap: 12px;
  align-items: start;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 15px;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.mobile-accordion__summary::after {
  width: 9px;
  height: 9px;
  margin-top: 7px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  color: var(--color-ink-soft);
  content: "";
  transform: rotate(45deg);
  transition: color 180ms ease, transform 220ms ease;
}

.mobile-accordion--open .mobile-accordion__summary::after {
  color: var(--color-yellow);
  transform: translateY(4px) rotate(225deg);
}

.mobile-accordion__summary > svg {
  width: 22px;
  height: 22px;
  color: var(--color-yellow);
}

.mobile-accordion__summary strong {
  display: block;
  color: var(--color-ink);
  font-size: 0.98rem;
  line-height: 1.28;
}

.mobile-accordion__summary span {
  display: block;
  margin-top: 6px;
  color: var(--color-muted);
  font-size: 0.9rem;
  line-height: 1.48;
}

.mobile-accordion ul {
  display: grid;
  gap: 9px;
  margin: 0;
  border-top: 1px solid var(--color-line);
  padding: 14px 15px 15px 49px;
  list-style: none;
}

.mobile-accordion li {
  position: relative;
  color: var(--color-ink-soft);
  font-size: 0.9rem;
  line-height: 1.48;
}

.mobile-accordion li::before {
  position: absolute;
  top: 0.7em;
  left: -16px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-yellow);
  content: "";
}

.mobile-ai__cards {
  display: grid;
  gap: 10px;
}

.mobile-ai__cards article {
  border: 1px solid var(--color-line);
  border-radius: 18px;
  background: #ffffff;
  padding: 16px;
}

.mobile-ai__cards article > svg {
  width: 22px;
  height: 22px;
  color: var(--color-yellow);
}

.mobile-ai__cards h3 {
  margin: 12px 0 0;
  color: var(--color-ink);
  font-size: 1.05rem;
}

.mobile-ai__cards p {
  margin: 8px 0 0;
  color: var(--color-muted);
  font-size: 0.92rem;
  line-height: 1.52;
}

.mobile-ai__cards article > div {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 14px;
}

.mobile-ai__cards article > div span {
  border: 1px solid #d8d1c5;
  border-radius: 999px;
  padding: 5px 8px;
  color: #4f4940;
  font-size: 0.72rem;
  font-weight: 850;
}

.mobile-card-list article,
.mobile-evidence article {
  border: 1px solid var(--color-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.22);
  padding: 15px;
}

.mobile-evidence article {
  display: grid;
  gap: 9px;
}

.mobile-card-list small,
.mobile-evidence small {
  display: block;
  margin-bottom: 2px;
  color: var(--color-muted);
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
}

.mobile-evidence__timeline {
  display: grid;
  gap: 10px;
}

.mobile-evidence h3 {
  margin: 0;
  color: var(--color-ink);
  font-size: 1rem;
  line-height: 1.28;
}

.mobile-evidence strong {
  display: block;
  color: var(--color-ink-soft);
  font-weight: 850;
}

.mobile-evidence article > p {
  margin-top: 0;
}

.mobile-evidence article > strong {
  margin-top: 2px;
  border-top: 1px solid rgba(20, 20, 20, 0.08);
  padding-top: 9px;
}

.mobile-contact__actions {
  display: grid;
  grid-template-columns: repeat(3, 58px);
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

.mobile-contact__person {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  margin-top: 24px;
  border-top: 1px solid var(--color-line);
  padding-top: 16px;
}

.mobile-contact__person figure {
  overflow: hidden;
  width: 56px;
  aspect-ratio: 2 / 3;
  margin: 0;
  border: 1px solid var(--color-line);
  border-radius: 12px;
  background: var(--color-paper);
  box-shadow: 0 13px 30px rgba(31, 28, 20, 0.1);
}

.mobile-contact__person img {
  display: block;
  width: 100%;
  height: 100%;
  filter: grayscale(1) sepia(0.08) saturate(0.85) contrast(1.04);
  object-fit: cover;
  object-position: center top;
}

.mobile-contact__person strong,
.mobile-contact__person span,
.mobile-contact__person small {
  display: block;
}

.mobile-contact__person strong {
  color: var(--color-ink);
  font-size: 0.98rem;
  font-weight: 900;
  line-height: 1.2;
}

.mobile-contact__person span {
  margin-top: 5px;
  color: var(--color-muted);
  font-size: 0.82rem;
  font-weight: 820;
  line-height: 1.42;
}

.mobile-contact__person small {
  margin-top: 4px;
  color: var(--color-muted);
  font-size: 0.76rem;
  font-weight: 650;
  line-height: 1.38;
}

.mobile-contact-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border: 1px solid var(--color-line);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.22);
  color: inherit;
  text-decoration: none;
  transition: background 180ms ease, border-color 180ms ease,
    transform 180ms ease;
}

.mobile-contact-action:active {
  transform: scale(0.96);
}

.mobile-contact-action__icon,
.mobile-contact-action__icon :deep(svg) {
  display: block;
  width: 22px;
  height: 22px;
  color: var(--color-muted);
  opacity: 0.78;
}

.mobile-disclosure-enter-active,
.mobile-disclosure-leave-active {
  max-height: 520px;
  overflow: hidden;
  transition: max-height 240ms ease, opacity 200ms ease, transform 200ms ease;
}

.mobile-disclosure-enter-from,
.mobile-disclosure-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 760px) {
  .mobile-home {
    display: block;
  }
}
</style>
